import { useState, useEffect, useRef } from "react";
import { lookupWord } from "../../utils/words";

export function MeaningQuiz({ words, onComplete, onSaveAndExit, onExit, initialIdx = 0, initialResults = [], initialStartTime }) {
  const QUIZ_COUNT = words.length;
  const [idx, setIdx] = useState(initialIdx);
  const [selected, setSelected] = useState(null);
  const [choices, setChoices] = useState(null);
  const [loading, setLoading] = useState(true);
  const defCacheRef = useRef({});
  const resultsRef = useRef(initialResults);
  const t0Ref = useRef(initialStartTime || Date.now());

  async function fetchDef(w) {
    if (w.id in defCacheRef.current) return defCacheRef.current[w.id];
    const result = await lookupWord(w.word);
    const def = result?.results?.[0]?.definition || null;
    defCacheRef.current[w.id] = def;
    return def;
  }

  async function buildChoices(targetWord) {
    const correctDef = await fetchDef(targetWord);
    if (!correctDef) return null;
    const others = words.filter(w => w.id !== targetWord.id).sort(() => Math.random() - 0.5).slice(0, 12);
    const distractors = [];
    for (const w of others) {
      if (distractors.length >= 3) break;
      const def = await fetchDef(w);
      if (def && def !== correctDef) distractors.push(def);
    }
    const options = [{ def: correctDef, correct: true }, ...distractors.map(d => ({ def: d, correct: false }))];
    options.sort(() => Math.random() - 0.5);
    return { correctDef, options };
  }

  useEffect(() => {
    setSelected(null); setChoices(null); setLoading(true);
    buildChoices(words[idx]).then(c => {
      setChoices(c);
      setLoading(false);
    });
  }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading && choices === null) {
      const skippedResult = { word: words[idx], correct: null, skipped: true };
      const newResults = [...resultsRef.current, skippedResult];
      resultsRef.current = newResults;
      if (idx + 1 >= QUIZ_COUNT) {
        onComplete(newResults, Math.round((Date.now() - t0Ref.current) / 1000));
      } else {
        setIdx(i => i + 1);
      }
    }
  }, [loading, choices]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSaveAndExit() {
    const saveIdx = selected !== null ? idx + 1 : idx;
    if (saveIdx >= QUIZ_COUNT) { onComplete(resultsRef.current, Math.round((Date.now() - t0Ref.current) / 1000)); return; }
    onSaveAndExit?.({ quizType: "meaning", currentIndex: saveIdx, results: resultsRef.current, words, startTime: t0Ref.current, savedAt: Date.now(), expiresAt: new Date().setHours(23,59,59,999) });
  }

  function handleSelect(i) {
    if (selected !== null) return;
    setSelected(i);
    const isCorrect = choices.options[i].correct;
    const newResult = { word: words[idx], correct: isCorrect };
    resultsRef.current = [...resultsRef.current, newResult];
  }

  function next() {
    if (idx + 1 >= QUIZ_COUNT) {
      onComplete(resultsRef.current, Math.round((Date.now() - t0Ref.current) / 1000));
    } else {
      setIdx(i => i + 1);
    }
  }

  const pct = Math.round((idx / QUIZ_COUNT) * 100);
  const word = words[idx];

  return (
    <div className="mq-screen">
      <div className="test-prog-bar"><div className="test-prog-fill" style={{ width: `${pct}%` }} /></div>
      <div className="mq-body">
        <div className="test-ctr">Question {idx + 1} of {QUIZ_COUNT}</div>
        <div className="mq-word">{word.word}</div>
        <div className="mq-prompt">Which definition is correct?</div>
        {loading
          ? <div className="mq-loading">🔍 Looking up definition…</div>
          : choices && (
            <>
              <div className="mq-choices">
                {choices.options.map((opt, i) => {
                  let cls = "mq-choice";
                  if (selected !== null) {
                    if (opt.correct) cls += " reveal";
                    else if (i === selected && !opt.correct) cls += " wrong";
                  }
                  return (
                    <button key={i} className={cls} disabled={selected !== null} onClick={() => handleSelect(i)}>
                      {opt.def}
                    </button>
                  );
                })}
              </div>
              {selected !== null && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: ".75rem", width: "100%" }}>
                  <div className={`fb-banner ${choices.options[selected].correct ? "ok" : "bad"}`} style={{ width: "100%" }}>
                    {choices.options[selected].correct ? "✅ Correct!" : `❌ The correct answer is highlighted above`}
                  </div>
                  <button className="next-btn" onClick={next}>
                    {idx + 1 >= QUIZ_COUNT ? "See Results →" : "Next Word →"}
                  </button>
                </div>
              )}
            </>
          )
        }
        <div style={{ display: "flex", gap: ".5rem", justifyContent: "center", marginTop: "1.25rem" }}>
          <button style={{ background: "var(--amber)", color: "#fff", border: "none", borderRadius: 20, padding: ".3rem .8rem", fontSize: ".73rem", fontWeight: 700, cursor: "pointer" }}
            onClick={handleSaveAndExit}>💾 Save & Exit</button>
          <button style={{ background: "transparent", color: "var(--muted)", border: "1px solid var(--border)", borderRadius: 20, padding: ".3rem .8rem", fontSize: ".73rem", fontWeight: 600, cursor: "pointer" }}
            onClick={() => onExit?.()}>✕ Exit</button>
        </div>
      </div>
    </div>
  );
}
